"use client";

import type { ReactNode } from "react";
import { useLocale, useTranslations } from "use-intl";

import { Link } from "@/components/link";
import { releaseCardAccessibility } from "@/components/release-card-accessibility";
import { ReleaseCardBody } from "@/components/release-card-body";
import { releaseCardModel } from "@/components/release-card-model";
import type { ReleaseCardVariant } from "@/components/release-card-variant-model";
import { useLists } from "@/contexts/lists-context";
import { useHydrated } from "@/hooks/use-hydrated";
import { cn } from "@/lib/utils";
import type { MediaFormatDetail, MediaFormatSummaryItem } from "@garimpa/lib/formats";

export interface ReleaseCardData {
  id: string;
  title: string;
  date: string | null;
  country: string | null;
  mediaFormatSummary?: MediaFormatSummaryItem[];
  mediaFormatDetails?: MediaFormatDetail[];
  mediaFormatDetailTags?: string[];
  formatText: string | null;
  mediaFeatures: string[];
  releasePolicySignals?: string[];
  hasNonOfficialStatus?: boolean;
  singleMediaFormat?: string | null;
  primaryMediaFormat?: string | null;
  additionalMediaFormats?: string[];
  trackCount: number;
  labelNames: string[];
  catnos: string[];
  notes: string | null;
  status?: string | null;
  imageCount?: number | null;
}

interface ReleaseCardProps {
  release: ReleaseCardData;
  variant?: ReleaseCardVariant;
  onClick?: () => void;
  containerClassName?: string;
  trailing?: ReactNode;
}

function releaseCardSavedBorderClass(inCollection: boolean, inWishlist: boolean) {
  if (inCollection) return "border-green-500/60 hover:border-green-400";
  if (inWishlist) return "border-pink-500/60 hover:border-pink-400";
  return "border-border hover:border-primary";
}

function releaseCardContainerClass(input: {
  containerClassName?: string;
  inCollection: boolean;
  inWishlist: boolean;
  showcase: boolean;
  variant: ReleaseCardVariant;
}) {
  return cn(
    "group relative flex gap-3 p-3 rounded-lg bg-secondary/50 border hover:bg-secondary transition-[background-color,border-color,box-shadow,transform] motion-reduce:transition-none text-left w-full h-full",
    input.variant === "full" && "md:gap-4 md:p-4",
    input.showcase &&
      "flex-col gap-0 overflow-hidden p-0 bg-card hover:bg-card hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/5",
    input.variant === "index" &&
      "flex-row gap-3 p-3 hover:translate-y-0 hover:shadow-none md:flex-col md:gap-0 md:p-0 md:hover:-translate-y-0.5 md:hover:shadow-xl",
    releaseCardSavedBorderClass(input.inCollection, input.inWishlist),
    input.containerClassName,
  );
}

export function ReleaseCard({
  release,
  variant = "full",
  onClick,
  containerClassName,
  trailing,
}: ReleaseCardProps) {
  const formatMessages = useTranslations("shared.formats");
  const featureMessages = useTranslations("shared.mediaFeatures");
  const locale = useLocale();
  const { isInCollection, isInWishlist } = useLists();
  const hydrated = useHydrated();
  const inCollection = hydrated && isInCollection({ releaseId: release.id });
  const inWishlist = hydrated && isInWishlist({ releaseId: release.id });
  const model = releaseCardModel({
    detailInput: { formatMessages, featureMessages },
    locale,
    release,
    summaryInput: { formatMessages },
    variant,
  });
  const accessibility = releaseCardAccessibility({
    catalogNumberCount: release.catnos.length,
    index: model.index,
    labelCount: release.labelNames.length,
    releaseId: release.id,
    title: release.title,
  });
  const containerClass = releaseCardContainerClass({
    containerClassName,
    inCollection,
    inWishlist,
    showcase: model.showcase,
    variant,
  });
  const body = (
    <ReleaseCardBody
      catalogNumberId={accessibility.catalogNumberId}
      headingId={accessibility.headingId}
      inCollection={inCollection}
      inWishlist={inWishlist}
      labelId={accessibility.labelId}
      model={model}
      release={release}
      trailing={trailing}
      variant={variant}
    />
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={containerClass}>
        {body}
      </button>
    );
  }
  if (model.showcase) {
    return (
      <div className={containerClass}>
        <Link
          to={`/release/${release.id}`}
          className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-describedby={accessibility.ariaDescribedBy}
          aria-label={accessibility.ariaLabel}
          aria-labelledby={accessibility.ariaLabelledBy}
        />
        {body}
      </div>
    );
  }
  return (
    <Link to={`/release/${release.id}`} className={containerClass}>
      {body}
    </Link>
  );
}
